import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState, type JSX, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, MoreHorizontal, Pencil, XCircle } from 'lucide-react';
import clsx from 'clsx';
import { apiRequest, ApiError } from '../lib/api';
import { useToast } from '../lib/toast';
import { useConfirm } from '../components/ui/Confirm';
import { useAuthStore } from '../lib/auth-store';
import { formatDate, formatINR, timeAgo } from '../lib/format';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { DetailHeader } from '../components/shared/DetailHeader';
import { KpiStrip, type KpiItem } from '../components/shared/KpiStrip';
import { ActivityPanel } from '../components/shared/ActivityPanel';
import { GatedSection } from '../components/shared/GatedSection';
import { ClaimStatusBadge, FeeStatusBadge } from '../components/finance/StatusBadge';
import { ClaimEditDrawer } from '../components/finance/ClaimEditDrawer';
import { ClaimRejectDrawer } from '../components/finance/ClaimRejectDrawer';
import {
  cycleLabel,
  type FeeCycle,
  type FeePayment,
  type FeePaymentClaim,
  type FeePaymentStudentRef,
} from '../components/finance/types';

export const Route = createFileRoute('/_authed/finance/fees/claims/$claimId')({
  component: ClaimDetailPage,
});

// ---------------------------------------------------------------------------
// Detail response shape — the backend agent is extending GET /api/fees/claims/:id
// to return embedded student / household / feePayment / claimedBy / confirmedBy.
// We type each embed as optional so the page renders gracefully against the
// in-progress backend and against the future enriched shape alike.
// ---------------------------------------------------------------------------

interface UserRef {
  id: string;
  displayName: string | null;
  email: string | null;
}

interface HouseholdRef {
  id: string;
  primaryContactName: string;
  primaryPhone: string | null;
}

interface FeePaymentWithCycle extends FeePayment {
  cycle?: FeeCycle | null;
}

interface ClaimDetail {
  claim: FeePaymentClaim;
  payment: FeePaymentWithCycle;
  student: FeePaymentStudentRef;
  household?: HouseholdRef | null;
  claimedBy?: UserRef | null;
  confirmedBy?: UserRef | null;
}

const ADMIN_ROLES = ['super_admin', 'finance_admin'] as const;

const DAY_MS = 1000 * 60 * 60 * 24;

const daysSince = (iso: string): number => {
  const created = new Date(iso).getTime();
  if (Number.isNaN(created)) return 0;
  return Math.max(0, Math.floor((Date.now() - created) / DAY_MS));
};

function ClaimDetailPage(): JSX.Element {
  const { claimId } = Route.useParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const role = useAuthStore((s) => s.user?.primaryRole ?? null);
  const isAdmin = role !== null && (ADMIN_ROLES as readonly string[]).includes(role);

  const [editOpen, setEditOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);

  const claimQuery = useQuery({
    queryKey: ['fee-claim', 'detail', claimId] as const,
    queryFn: ({ signal }) =>
      apiRequest<ClaimDetail>(
        `/api/fees/claims/${encodeURIComponent(claimId)}`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['fee-claim', 'detail', claimId] });
    void queryClient.invalidateQueries({ queryKey: ['feeClaims'] });
    void queryClient.invalidateQueries({ queryKey: ['feePayments'] });
  };

  const confirmMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true }>(
        `/api/fees/claims/${encodeURIComponent(claimId)}/confirm`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      toast('Claim confirmed and payment marked as paid.', { variant: 'success' });
      invalidate();
    },
    onError: (err: unknown) => {
      toast(err instanceof ApiError ? err.message : 'Could not confirm claim.', {
        variant: 'error',
      });
    },
  });

  const data = claimQuery.data;
  const claim = data?.claim;
  const payment = data?.payment;
  const student = data?.student;
  const cycle = data?.payment.cycle ?? null;

  useEffect(() => {
    // Close overflow when clicking outside.
    if (!overflowOpen) return;
    const handler = (e: MouseEvent): void => {
      const t = e.target as HTMLElement | null;
      if (!t?.closest('[data-claim-overflow]')) setOverflowOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [overflowOpen]);

  const handleConfirm = async (): Promise<void> => {
    if (!claim || !payment || !student) return;
    const ok = await confirm({
      title: 'Confirm reported payment?',
      description: `Mark the payment of ${formatINR(payment.netAmountPaise)} from ${
        student.name
      } as received. A receipt will be issued.`,
      confirmLabel: 'Confirm payment',
    });
    if (!ok) return;
    confirmMutation.mutate();
  };

  const kpiItems = useMemo<KpiItem[]>(() => {
    if (!claim || !payment) return [];
    const cycleText = cycle ? cycleLabel(cycle.year, cycle.month) : '—';
    const waiting = daysSince(claim.createdAt);
    const waitingValue: ReactNode =
      claim.status === 'pending' ? (
        <span className={clsx(waiting > 5 && 'text-[color:var(--color-danger)]')}>
          {waiting}
        </span>
      ) : (
        waiting
      );
    return [
      { label: 'Amount claimed', value: formatINR(payment.netAmountPaise) },
      { label: 'Cycle', value: cycleText },
      {
        label: 'Days waiting',
        value: waitingValue,
        sub: claim.status === 'pending' ? 'since submitted' : 'resolved',
      },
      {
        label: 'Payment status',
        value: <FeeStatusBadge status={payment.status} />,
      },
    ];
  }, [claim, payment, cycle]);

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto w-full">
      <GatedSection
        error={claimQuery.error}
        title="Reported payment"
        onRetry={() => claimQuery.refetch()}
      >
        {claimQuery.isLoading || !data || !claim || !payment || !student ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            <DetailHeader
              title={`Claim from ${student.name}`}
              sublabel={
                <>
                  <span>Submitted</span>
                  <span
                    className="font-medium text-[color:var(--color-text)]"
                    title={formatDate(claim.createdAt, { withTime: true })}
                  >
                    {timeAgo(claim.createdAt)}
                  </span>
                  <span aria-hidden="true">·</span>
                  <span>
                    by{' '}
                    <span className="font-medium text-[color:var(--color-text)]">
                      {data.claimedBy?.displayName ??
                        claim.claimedByName ??
                        data.claimedBy?.email ??
                        '—'}
                    </span>
                  </span>
                </>
              }
              statusChip={<ClaimStatusBadge status={claim.status} />}
              backTo="/finance/fees/claims"
              actions={
                isAdmin ? (
                  <>
                    {claim.status === 'pending' ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        icon={<Pencil size={14} aria-hidden="true" />}
                        onClick={() => setEditOpen(true)}
                      >
                        Moderate
                      </Button>
                    ) : null}
                    {claim.status === 'pending' ? (
                      <Button
                        size="sm"
                        icon={<CheckCircle2 size={14} aria-hidden="true" />}
                        onClick={() => {
                          void handleConfirm();
                        }}
                        loading={confirmMutation.isPending}
                      >
                        Confirm payment
                      </Button>
                    ) : null}
                    {claim.status === 'pending' ? (
                      <Button
                        size="sm"
                        variant="danger"
                        icon={<XCircle size={14} aria-hidden="true" />}
                        onClick={() => setRejectOpen(true)}
                      >
                        Reject
                      </Button>
                    ) : null}
                    <ClaimOverflowMenu
                      open={overflowOpen}
                      onOpenChange={setOverflowOpen}
                      paymentId={payment.id}
                    />
                  </>
                ) : null
              }
            />

            <KpiStrip items={kpiItems} />

            <div className="grid gap-4 lg:grid-cols-2">
              <ClaimDetailsCard claim={claim} />
              <ParentCard claimedByName={data.claimedBy?.displayName ?? claim.claimedByName ?? null} claimedByEmail={data.claimedBy?.email ?? null} />
              <StudentHouseholdCard student={student} household={data.household ?? null} />
              <UnderlyingPaymentCard
                payment={payment}
                cycle={cycle}
              />
              {claim.status !== 'pending' ? (
                <ResolutionCard
                  status={claim.status}
                  confirmedBy={data.confirmedBy ?? null}
                  confirmedAt={claim.confirmedAt}
                  rejectedReason={claim.rejectedReason}
                />
              ) : null}
            </div>

            <ActivityPanel
              targetType="fee_payment_claim"
              targetId={claim.id}
              title="Activity"
            />
          </div>
        )}
      </GatedSection>

      {isAdmin && claim ? (
        <ClaimEditDrawer
          claim={editOpen ? claim : null}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            invalidate();
          }}
        />
      ) : null}

      {isAdmin && claim ? (
        <ClaimRejectDrawer
          claimId={rejectOpen ? claim.id : null}
          studentName={student?.name}
          onClose={() => setRejectOpen(false)}
          onSuccess={() => {
            setRejectOpen(false);
            invalidate();
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overflow menu
// ---------------------------------------------------------------------------

interface ClaimOverflowMenuProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  paymentId: string;
}

function ClaimOverflowMenu({
  open,
  onOpenChange,
  paymentId,
}: ClaimOverflowMenuProps): JSX.Element {
  // The fee_payment detail route (/finance/fees/payments/$paymentId) is
  // shipping in a sibling slice; until the route is registered in the typed
  // route tree we navigate via a plain anchor so this file typechecks today.
  return (
    <div className="relative" data-claim-overflow>
      <Button
        variant="ghost"
        size="sm"
        icon={<MoreHorizontal size={14} aria-hidden="true" />}
        onClick={() => onOpenChange(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        More
      </Button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-9 z-10 min-w-[200px] rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] py-1 shadow-lg"
        >
          <a
            role="menuitem"
            href={`/finance/fees/payments/${encodeURIComponent(paymentId)}`}
            onClick={() => onOpenChange(false)}
            className="block w-full px-3 py-2 text-left text-sm text-[color:var(--color-text)] no-underline hover:bg-[color:var(--color-surface)] focus:outline-none focus-visible:bg-[color:var(--color-surface)]"
          >
            View payment record
          </a>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

interface CardProps {
  title: string;
  children: ReactNode;
}

const Card = ({ title, children }: CardProps): JSX.Element => (
  <section className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
    <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
      {title}
    </h3>
    <div className="mt-3 space-y-2 text-sm text-[color:var(--color-text)]">
      {children}
    </div>
  </section>
);

const Field = ({
  label,
  value,
}: {
  label: string;
  value: ReactNode;
}): JSX.Element => (
  <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
    <dt className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
      {label}
    </dt>
    <dd className="min-w-0 text-right text-sm text-[color:var(--color-text)]">
      {value ?? <span className="text-[color:var(--color-text-muted)]">—</span>}
    </dd>
  </div>
);

const VIA_LABELS: Record<string, string> = {
  cash: 'Cash',
  bank_transfer: 'Bank transfer',
  upi_offline: 'UPI (offline)',
  cheque: 'Cheque',
  other: 'Other',
};

function ClaimDetailsCard({ claim }: { claim: FeePaymentClaim }): JSX.Element {
  return (
    <Card title="Claim details">
      <dl className="space-y-2">
        <Field
          label="Claimed paid at"
          value={formatDate(claim.claimedPaidAt, { withTime: true })}
        />
        <Field
          label="Paid via"
          value={VIA_LABELS[claim.claimedPaidVia] ?? claim.claimedPaidVia}
        />
        <Field
          label="Reference"
          value={
            claim.reference ? (
              <span className="font-mono text-xs">{claim.reference}</span>
            ) : null
          }
        />
        <Field
          label="Notes"
          value={
            claim.notes ? (
              <span className="whitespace-pre-wrap text-left">{claim.notes}</span>
            ) : null
          }
        />
        <Field label="Submitted" value={formatDate(claim.createdAt, { withTime: true })} />
      </dl>
    </Card>
  );
}

interface ParentCardProps {
  claimedByName: string | null;
  claimedByEmail: string | null;
}

function ParentCard({ claimedByName, claimedByEmail }: ParentCardProps): JSX.Element {
  return (
    <Card title="Parent (claimed by)">
      <p className="text-sm font-medium text-[color:var(--color-text)]">
        {claimedByName ?? '—'}
      </p>
      {claimedByEmail ? (
        <p className="text-xs text-[color:var(--color-text-muted)]">
          {claimedByEmail}
        </p>
      ) : null}
    </Card>
  );
}

interface StudentHouseholdCardProps {
  student: FeePaymentStudentRef;
  household: HouseholdRef | null;
}

function StudentHouseholdCard({
  student,
  household,
}: StudentHouseholdCardProps): JSX.Element {
  return (
    <Card title="Student & family">
      <div>
        <p className="text-sm font-medium text-[color:var(--color-text)]">
          <Link
            to="/people/students/$studentId"
            params={{ studentId: student.id }}
            className="hover:underline"
          >
            {student.name}
          </Link>
        </p>
        {student.admissionNumber ? (
          <p className="text-xs text-[color:var(--color-text-muted)]">
            {student.admissionNumber}
          </p>
        ) : null}
      </div>
      <div className="border-t border-[color:var(--color-border)] pt-2">
        <p className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
          Family
        </p>
        {household ? (
          <p className="text-sm">
            <Link
              to="/people/households/$householdId"
              params={{ householdId: household.id }}
              className="font-medium text-[color:var(--color-text)] hover:underline"
            >
              {household.primaryContactName}
            </Link>
            {household.primaryPhone ? (
              <span className="ml-2 text-xs text-[color:var(--color-text-muted)]">
                {household.primaryPhone}
              </span>
            ) : null}
          </p>
        ) : (
          <p className="text-sm text-[color:var(--color-text-muted)]">
            No family linked.
          </p>
        )}
      </div>
    </Card>
  );
}

interface UnderlyingPaymentCardProps {
  payment: FeePaymentWithCycle;
  cycle: FeeCycle | null;
}

function UnderlyingPaymentCard({
  payment,
  cycle,
}: UnderlyingPaymentCardProps): JSX.Element {
  return (
    <Card title="Underlying payment">
      <dl className="space-y-2">
        <Field
          label="Cycle"
          value={cycle ? cycleLabel(cycle.year, cycle.month) : '—'}
        />
        <Field label="Net amount" value={formatINR(payment.netAmountPaise)} />
        {payment.discountPaise > 0 ? (
          <Field label="Discount" value={formatINR(payment.discountPaise)} />
        ) : null}
        <Field
          label="Due date"
          value={formatDate(`${payment.dueDate}T00:00:00Z`)}
        />
        <Field
          label="Status"
          value={<FeeStatusBadge status={payment.status} />}
        />
        <Field
          label="Receipt #"
          value={
            payment.receiptNumber ? (
              <span className="font-mono text-xs">{payment.receiptNumber}</span>
            ) : null
          }
        />
      </dl>
      <div className="mt-3">
        <a
          href={`/finance/fees/payments/${encodeURIComponent(payment.id)}`}
          className="inline-flex items-center gap-1 text-xs font-medium text-gold-700 hover:underline"
        >
          View payment record
        </a>
      </div>
    </Card>
  );
}

interface ResolutionCardProps {
  status: FeePaymentClaim['status'];
  confirmedBy: UserRef | null;
  confirmedAt: string | null;
  rejectedReason: string | null;
}

function ResolutionCard({
  status,
  confirmedBy,
  confirmedAt,
  rejectedReason,
}: ResolutionCardProps): JSX.Element {
  return (
    <Card title="Resolution">
      <dl className="space-y-2">
        <Field
          label={status === 'rejected' ? 'Rejected by' : 'Confirmed by'}
          value={
            confirmedBy?.displayName ?? confirmedBy?.email ?? (
              <span className="text-[color:var(--color-text-muted)]">—</span>
            )
          }
        />
        <Field
          label={status === 'rejected' ? 'Rejected at' : 'Confirmed at'}
          value={
            confirmedAt ? formatDate(confirmedAt, { withTime: true }) : null
          }
        />
        {status === 'rejected' && rejectedReason ? (
          <div>
            <p className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
              Reason
            </p>
            <p className="mt-1 whitespace-pre-wrap text-sm text-[color:var(--color-text)]">
              {rejectedReason}
            </p>
          </div>
        ) : null}
      </dl>
    </Card>
  );
}
